import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Dismiss24Regular, Open16Regular } from '@fluentui/react-icons'
import type { HelpSection } from '../shell.types'
import type { ShellTelemetry } from '../telemetry/shellTelemetry'

const useStyles = makeStyles({
  drawer: {
    width: '520px',
    maxWidth: '100vw',
  },
  header: {
    boxSizing: 'border-box',
    height: '80px',
    flexShrink: 0,
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL} ${tokens.spacingVerticalL} 30px`,
    borderBottom: `${tokens.strokeWidthThin} solid #e5e5e5`,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  body: {
    padding: `${tokens.spacingVerticalS} 30px ${tokens.spacingVerticalXL}`,
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    paddingBottom: 0,
  },
  sectionAfterFirst: {
    marginTop: '22px',
    paddingTop: '17px',
    borderTop: `${tokens.strokeWidthThin} solid #e5e5e5`,
  },
  sectionTitle: {
    color: tokens.colorNeutralForeground2,
    fontSize: '15px',
    fontWeight: 650,
    lineHeight: 'normal',
    margin: `0 0 ${tokens.spacingVerticalMNudge}`,
  },
  links: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  link: {
    minHeight: '39px',
    alignItems: 'center',
    display: 'flex',
    gap: '7px',
    width: 'fit-content',
    color: tokens.colorBrandForeground1,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': {
      color: '#004578',
      textDecorationLine: 'underline',
    },
  },
  externalIcon: {
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  callout: {
    marginTop: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalXL} 0 0`,
    borderTop: `${tokens.strokeWidthThin} solid #dedede`,
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
    lineHeight: '1.6',
  },
  calloutTitle: {
    color: tokens.colorNeutralForeground2,
    display: 'block',
    fontSize: tokens.fontSizeBase300,
    fontWeight: 650,
    marginBottom: 0,
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalL,
  },
  footerLink: {
    fontSize: '13px',
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  footerDivider: {
    color: '#8a8886',
  },
})

interface HelpPanelProps {
  open: boolean
  sections: HelpSection[]
  telemetry: ShellTelemetry
  onOpenChange: (open: boolean) => void
}

export const HelpPanel = ({
  open,
  sections,
  telemetry,
  onOpenChange,
}: HelpPanelProps) => {
  const styles = useStyles()

  return (
    <Drawer
      className={styles.drawer}
      open={open}
      position="end"
      type="overlay"
      onOpenChange={(_, data) => onOpenChange(data.open)}
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle
          className={styles.headerTitle}
          action={
            <Button
              appearance="subtle"
              aria-label="Close help"
              icon={<Dismiss24Regular />}
              onClick={() => onOpenChange(false)}
            />
          }
        >
          Help
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        <div className={styles.layout}>
          <div className={styles.sections}>
            {sections.map((section, sectionIndex) => (
              <section
                className={`${styles.section} ${
                  sectionIndex > 0 ? styles.sectionAfterFirst : ''
                }`}
                key={section.remoteName}
              >
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                <div className={styles.links}>
                  {section.links.map((link, linkIndex) => (
                    <Link
                      className={styles.link}
                      href={link.href}
                      key={`${section.remoteName}-${link.label}-${linkIndex}`}
                      rel="noopener noreferrer"
                      target="_blank"
                      onClick={() =>
                        telemetry.event('help.link.click', {
                          remoteName: section.remoteName,
                          label: link.label,
                        })
                      }
                    >
                      {link.label}
                      <Open16Regular
                        aria-hidden="true"
                        className={styles.externalIcon}
                      />
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
          <div className={styles.callout}>
            <strong className={styles.calloutTitle}>Need additional help?</strong>
            Our FrontDesk team (
            <Link
              href="https://aka.ms/FrontDesk"
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => telemetry.event('help.frontdesk.click')}
            >
              aka.ms/FrontDesk
            </Link>
            ) is ready to assist with user support, architectural consultation,
            application development, and PowerApps ownership transfers.
          </div>
          <footer className={styles.footer}>
            <Link
              className={styles.footerLink}
              href="https://www.microsoft.com/en-us/legal/terms-of-use"
              rel="noopener noreferrer"
              target="_blank"
            >
              Legal
            </Link>
            <span aria-hidden="true" className={styles.footerDivider}>
              |
            </span>
            <Link
              className={styles.footerLink}
              href="https://www.microsoft.com/en-US/privacy/data-privacy-notice"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy
            </Link>
          </footer>
        </div>
      </DrawerBody>
    </Drawer>
  )
}
